import { useCallback, useEffect, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';

import { useSaveDiscoveryConfigMutation } from '../../ManageCMDBDiscoveredApps/data-store/saveDiscoveryConfigSlice';
import {
  buildCron,
  formatTimeLabel,
  parseSchedulingCron,
} from '../utils/schedulingUtils';

interface UseSchedulingParams {
  cronString: string;
  status: string;
  instanceKey: number;
  sortOrder: number;
  onDirtyChange: (isDirty: boolean) => void;
  onSaveError: (message: string) => void;
  onSaveSuccess?: (message: string) => void;
  resetVersion: number;
}

/**
 * Hook for managing scheduling state and save operations.
 */
export default function useScheduling({
  cronString,
  status,
  instanceKey,
  sortOrder,
  onDirtyChange,
  onSaveError,
  onSaveSuccess,
  resetVersion,
}: UseSchedulingParams) {
  // Parse initial cron into usable variables
  const {
    minute: minuteStr,
    hour: hourStr,
    interval: initialInterval,
  } = parseSchedulingCron(cronString);

  const minute = parseInt(minuteStr, 10);
  const hour = parseInt(hourStr, 10);

  // Local UI state for repeat interval
  const [repeatInterval, setRepeatInterval] = useState(initialInterval);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Track initial values for change detection
  const [initialRepeatInterval, setInitialRepeatInterval] =
    useState(initialInterval);

  // Ref to avoid stale callbacks
  const onDirtyRef = useRef(onDirtyChange);
  const onErrorRef = useRef(onSaveError);
  const onSuccessRef = useRef(onSaveSuccess);

  useEffect(() => {
    onDirtyRef.current = onDirtyChange;
    onErrorRef.current = onSaveError;
    onSuccessRef.current = onSaveSuccess;
  }, [onDirtyChange, onSaveError, onSaveSuccess]);

  // Track initialization to suppress marking dirty on seed/reset
  const isInitializing = useRef(true);

  useEffect(() => {
    setRepeatInterval(initialInterval);
    setInitialRepeatInterval(initialInterval);
    setIsDirty(false);
    onDirtyRef.current(false);
    isInitializing.current = true;
  }, [initialInterval, resetVersion]);
  useEffect(() => {
    if (isInitializing.current) {
      isInitializing.current = false;
    }
  }, [repeatInterval]);

  // Mutation hook for saving config
  const [saveConfig, { isLoading }] = useSaveDiscoveryConfigMutation();

  /**
   * Handler for interval changes.
   */
  const onIntervalChange = useCallback(
    (value: number) => {
      setRepeatInterval(value);
      const hasChanges = value !== initialRepeatInterval;

      setIsDirty(hasChanges);

      if (!isInitializing.current) {
        onDirtyRef.current(hasChanges);
      }
    },
    [initialRepeatInterval]
  );

  /**
   * Saves scheduling configuration.
   */
  const saveScheduling = useCallback(async () => {
    const newCron = buildCron({
      minute: minute.toString(),
      hour: hour.toString(),
      interval: repeatInterval,
    });

    try {
      const response = await saveConfig({
        id: instanceKey,
        tabs: [
          { sortOrder, name: 'Scheduling', details: { cron_string: newCron } },
        ],
      }).unwrap();

      onErrorRef.current('');
      onDirtyRef.current(false);
      setIsDirty(false);
      setInitialRepeatInterval(repeatInterval);
      onSuccessRef.current?.(
        response.message || 'Scheduling saved successfully'
      );
    } catch (err: unknown) {
      Logger.error('Failed to save scheduling:', err);
      const errorData = err as { data?: { message?: string } };
      const message = errorData.data?.message ?? 'Save failed';

      onErrorRef.current(message);
    }
  }, [minute, hour, repeatInterval, saveConfig, instanceKey, sortOrder]);

  return {
    repeatInterval,
    timeLabel: formatTimeLabel(hour, minute),
    isDisabled: status === 'Disabled',
    isSaveDisabled: status === 'Disabled' || isEmpty || !isDirty,
    isLoading,
    onIntervalChange,
    onEmptyChange: setIsEmpty,
    saveScheduling,
  };
}
